import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../components/Panel'
import { visitorsApi, type SessionFilters } from '../../api/visitors'
import { fmtDateTime, fmtInt, PUBLICATION_NAMES } from '../../lib/format'
import { duration, STEP_LABEL } from '../../lib/visitorsFormat'
import { describeFilters, filtersFromSearch } from '../../lib/visitorsFilters'
import { useVisitorsRange } from '../../lib/useVisitorsRange'
import { useMemberAccess } from './engagementBits'
import { QueryProblem, Skeleton, VisitorsFrame, linkCls, tdn, th, thr } from './VisitorsFrame'

const PAGE = 50
const VERDICTS = [
  { v: 'human', label: 'Human' },
  { v: 'unconfirmed', label: 'Unconfirmed' },
  { v: 'automated', label: 'Automated' },
  { v: 'all', label: 'All' },
]
const TONE = { human: 'text-radar-green', unconfirmed: 'text-amber-glow', automated: 'text-radar-red' } as const

export function VisitorsSessions() {
  const [sp] = useSearchParams()
  // Changing any filter or the range remounts the list at page one.
  return <SessionsList key={sp.toString()} />
}

function SessionsList() {
  const { pub = '' } = useParams()
  const { range, search } = useVisitorsRange()
  const [sp, setSp] = useSearchParams()
  const [offset, setOffset] = useState(0)
  const { canMember } = useMemberAccess()
  const okPub = Object.hasOwn(PUBLICATION_NAMES, pub)
  const filters = filtersFromSearch(sp)
  const q = useQuery({
    queryKey: ['visitors-sessions', pub, range.from, range.to, filters, offset],
    queryFn: () => visitorsApi.sessions(pub, range, filters, PAGE, offset),
    enabled: okPub,
    placeholderData: (prev) => prev,
  })
  const base = `/visitors/${pub}`
  const chips = (Object.entries(filters) as [keyof SessionFilters, string][]).flatMap(([k, v]) =>
    describeFilters({ [k]: v }).map((text) => ({ k, text })),
  )
  const drop = (k: string) => {
    const next = new URLSearchParams(sp)
    next.delete(k)
    setSp(next)
  }
  const setVerdict = (v: string) => {
    const next = new URLSearchParams(sp)
    if (v === 'human') next.delete('verdict')
    else next.set('verdict', v)
    setSp(next)
  }
  const d = q.data
  const from = d && d.total > 0 ? offset + 1 : 0
  const to = d ? Math.min(offset + PAGE, d.total) : 0

  return (
    <VisitorsFrame pub={pub} valid={okPub} title="Visits" crumbs={[{ label: 'Visits' }]}>
      <div className="flex flex-wrap items-center gap-2">
        {chips.map((c) => (
          <button
            key={c.k}
            type="button"
            onClick={() => drop(c.k)}
            aria-label={`Remove filter: ${c.text}`}
            className="min-h-[32px] px-3 font-mono text-[12px] border border-ink-500 text-paper hover:bg-ink-700"
          >
            {c.text} <span aria-hidden="true">×</span>
          </button>
        ))}
        <label className="flex items-center gap-2 ml-auto">
          <span className="label">Verdict</span>
          <select
            value={filters.verdict ?? 'human'}
            onChange={(e) => setVerdict(e.target.value)}
            className="min-h-[32px] bg-ink-900 border border-ink-500 px-2 font-mono text-[12px] text-paper"
          >
            {VERDICTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </label>
      </div>

      {q.isError ? (
        <QueryProblem error={q.error} pub={pub} />
      ) : !d ? (
        <Skeleton h="h-64" />
      ) : (
        <Panel accent="amber" bodyClassName="p-0">
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-[12px] text-paper">
              <thead>
                <tr className="border-b border-ink-500">
                  {['Started', 'Visitor', 'Member', 'Channel', 'Landing'].map((h) => <th key={h} className={th}>{h}</th>)}
                  {['Pages', 'Engaged'].map((h) => <th key={h} className={thr}>{h}</th>)}
                  {['Step', 'Verdict'].map((h) => <th key={h} className={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {d.items.map((r) => (
                  <tr key={r.session_id} className="border-b border-ink-500/50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Link to={`${base}/sessions/${encodeURIComponent(r.session_id)}?${search}`} className={linkCls}>{fmtDateTime(r.started_at)}</Link>
                    </td>
                    <td className="px-3 py-2" title={r.visitor_id}>
                      <Link to={`${base}/visitors/${encodeURIComponent(r.visitor_id)}?${search}`} className={linkCls}>{r.visitor_id.slice(0, 8)}</Link>
                    </td>
                    <td className="px-3 py-2">
                      {r.subscriber_id == null ? '—' : canMember
                        ? <Link to={`${base}/members/${r.subscriber_id}?${search}`} className={linkCls}>#{r.subscriber_id}</Link>
                        : `#${r.subscriber_id}`}
                    </td>
                    <td className="px-3 py-2">{r.channel ?? '—'}</td>
                    <td className="px-3 py-2 max-w-[260px] truncate" title={r.landing_path ?? ''}>{r.landing_path ?? '—'}</td>
                    <td className={tdn}>{fmtInt(r.page_count)}</td>
                    <td className={tdn}>{duration(r.engaged_ms)}</td>
                    <td className="px-3 py-2">{r.max_funnel_step ? STEP_LABEL[r.max_funnel_step] : '—'}</td>
                    <td className={`px-3 py-2 ${TONE[r.verdict]}`} title={r.verdict_reason ?? ''}>{r.verdict}</td>
                  </tr>
                ))}
                {d.items.length === 0 && <tr><td colSpan={9} className="px-3 py-4 text-paper-faint">No visits match these filters</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 px-3 py-3 font-mono text-[12px] text-paper-dim">
            <span>{fmtInt(from)}–{fmtInt(to)} of {fmtInt(d.total)}</span>
            <span className="flex gap-2">
              <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="min-h-[32px] px-3 border border-ink-500 hover:bg-ink-700 disabled:opacity-40">Prev</button>
              <button type="button" disabled={offset + PAGE >= d.total} onClick={() => setOffset(offset + PAGE)} className="min-h-[32px] px-3 border border-ink-500 hover:bg-ink-700 disabled:opacity-40">Next</button>
            </span>
          </div>
        </Panel>
      )}
    </VisitorsFrame>
  )
}
